import { paths, type StaffMember } from '@acs/shared';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { staffConverter, type StaffDoc } from './converters.js';
import { db } from './firebase.js';

/** Live list of a business's staff, ordered by the auto-assignment priority. */
export function subscribeStaff(businessId: string, cb: (rows: StaffDoc[]) => void): () => void {
  const q = query(
    collection(db, paths.staff(businessId)).withConverter(staffConverter),
    orderBy('sortOrder', 'asc'),
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

/** Create or replace a staff member. Pass an id to update, omit to create.
 * Returns the document id (useful to navigate into a freshly created member). */
export async function upsertStaffMember(
  businessId: string,
  staff: StaffMember,
  staffId?: string,
): Promise<string> {
  const ref = staffId
    ? doc(db, paths.staffMember(businessId, staffId))
    : doc(collection(db, paths.staff(businessId)));
  await setDoc(ref, staff, { merge: true });
  return ref.id;
}

export async function deleteStaffMember(businessId: string, staffId: string): Promise<void> {
  await deleteDoc(doc(db, paths.staffMember(businessId, staffId)));
}

/** Persist a reordering (drag to reorder) — writes each member's new sortOrder. */
export async function reorderStaff(businessId: string, orderedIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  orderedIds.forEach((id, i) => {
    batch.set(doc(db, paths.staffMember(businessId, id)), { sortOrder: i }, { merge: true });
  });
  await batch.commit();
}
